import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import InlineTable, { InlineColDef } from '../util/InlineTable';

interface Car {
  id: number;
  brand: string;
  model: string;
  plate_number: string;
  year: number | null;
  mileage: number | null;
  insurance_document_url?: string;
  is_active?: boolean;
}

interface CarsTableProps {
  baseUrl: string;
  unit: any;
}

const CarsTable: React.FC<CarsTableProps> = ({ baseUrl, unit }) => {
  const [rows, setRows] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('cars');
      const arr: Car[] = Array.isArray(data) ? data : [];
      arr.sort((a, b) => (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0));
      setRows(arr);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (row: Car) => {
    await axios.put(`cars/${row.id}`, { is_active: !row.is_active });
    await load();
  };

  const columns: InlineColDef<Car>[] = [
    {
      key: 'is_active',
      label: t('Status') || 'Status',
      type: 'readonly',
      width: 80,
      editable: false,
      render: (row) => (
        <span
          onClick={(e) => { e.stopPropagation(); toggleActive(row); }}
          title={row.is_active === false ? (t('Activate') || 'Set active') : (t('Deactivate') || 'Set inactive')}
          style={{
            cursor: 'pointer',
            fontSize: 10,
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: 6,
            whiteSpace: 'nowrap',
            background: row.is_active === false ? 'var(--btn-user-in)' : 'var(--color-success-bg)',
            color: row.is_active === false ? 'var(--text-color-calendar-week)' : 'var(--color-success)',
          }}
        >
          {row.is_active === false ? (t('Idle') || 'Idle') : (t('Active') || 'Active')}
        </span>
      ),
    },
    { key: 'brand',        label: t('Brand')        || 'Brand',        type: 'text',   placeholder: 'Ford…' },
    { key: 'model',        label: t('Model')        || 'Model',        type: 'text',   placeholder: 'Transit…' },
    { key: 'plate_number', label: t('PlateNumber')  || 'Plate',        type: 'text',   placeholder: 'AB12 CDE' },
    { key: 'year',         label: t('Year')         || 'Year',         type: 'number', width: 80 },
    { key: 'mileage',      label: t('Mileage')      || 'Mileage',      type: 'number', width: 90 },
    {
      key: 'insurance_document_url',
      label: t('Doc') || 'Doc',
      type: 'readonly',
      width: 50,
      editable: false,
      render: (row) => row.insurance_document_url
        ? <a href={`${baseUrl}${row.insurance_document_url}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 16 }}>📄</a>
        : <span style={{ opacity: 0.3 }}>—</span>,
    },
  ];

  const handleAdd = async (row: Partial<Car>) => {
    if (!row.brand?.trim() || !row.model?.trim() || !row.plate_number?.trim()) return;
    await axios.post('cars/add', {
      brand: row.brand.trim(),
      model: row.model.trim(),
      plate_number: row.plate_number.trim(),
      year: row.year ?? null,
      mileage: row.mileage ?? null,
    });
    await load();
  };

  const handleEdit = async (id: string | number, row: Partial<Car>) => {
    await axios.put(`cars/${id}`, {
      brand: row.brand,
      model: row.model,
      plate_number: row.plate_number,
      year: row.year ?? null,
      mileage: row.mileage ?? null,
      is_active: row.is_active,
    });
    await load();
  };

  const handleDelete = async (id: string | number) => {
    if (!window.confirm(t('AreYouSure') || 'Are you sure?')) return;
    await axios.delete(`cars/${id}`);
    await load();
  };

  return (
    <>
      <div className="bg-[var(--bg-form)] rounded-[5px]" style={{ border: 'var(--border-input)', overflow: 'hidden' }}>
        <InlineTable<Car>
          columns={columns}
          rows={rows}
          getKey={r => r.id}
          loading={loading}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          addLabel={`+ ${t('Cars') || 'Car'}`}
          emptyText={t('Empty') || 'No cars'}
        />
      </div>
    </>
  );
};

export default CarsTable;
